/**
 * Every page in admin mode, under `/admin/*`.
 *
 * One tree, not three. The app used to address the same pages three ways — a
 * top-level route, an `/admin/*` copy and a region-rooted `/r/:slug/*` copy —
 * and which one you got depended on the persona the chrome had derived for
 * you. The mode is the tree now: `/admin/*` is the organizational view and
 * `/me/*` is the instructor's, the address says which, and a page that scopes
 * itself to a region does it with `?region=` rather than with a second URL.
 *
 * Every page keeps its own permission gate. `RequireMode` above this only
 * decides who may enter the workspace.
 */
import { Route, Routes } from 'react-router-dom';

import BookingDetailPage from './components/bookings/BookingDetailPage';
import BookingPage from './components/bookings/BookingPage';
import { READ_BOOKINGS_PERMISSIONS } from './components/bookings/bookingPermissions';
import CustomerManagement from './components/customers/CustomerManagement';
import { adminDashboardsRoutes } from './components/dashboards/admin/admin-dashboards-routes';
import { venuesRoutes } from './components/dashboards/venues/venuesRoutes';
import { OverviewDashboard } from './components/dashboards/workshops/overview/OverviewDashboard';
import DataPage from './components/data/DataPage';
import { giftCardsRoutes } from './components/gift-cards/giftCardsRoutes';
import { giftPassBundlesRoutes } from './components/gift-pass-bundles/gift-pass-bundles-routes';
import { giftPassesRoutes } from './components/gift-passes/gift-passes-routes';
import InventoryPage from './components/inventory/InventoryPage';
import MarketingPage from './components/marketing/MarketingPage';
import PrivateEventDetailPage from './components/private-events/PrivateEventDetailPage';
import PrivateEventsPage from './components/private-events/PrivateEventsPage';
import { reviewRoutes } from './components/review/reviewRoutes';
import SettingsPage from './components/settings/SettingsPage';
import StaffEditPage from './components/staff/StaffEditPage';
import StaffManagement from './components/staff/StaffManagement';
import { ticketSalesCampaignsRoutes } from './components/ticket-sales-campaigns/ticket-sales-campaigns-routes';
import HomeRedirect from './shared/nav/HomeRedirect';
import NotFoundPage from './shared/NotFoundPage';
import RequirePermission from './shared/RequirePermission';
import { Permission } from './types/permissions';

/** Booking list + detail, both behind the read gate. Grouped as a fragment so
 *  the route table stays under the per-function line cap, matching the
 *  reviewRoutes / venuesRoutes / giftCardsRoutes pattern. */
const bookingRoutes = (
  <>
    <Route
      path="bookings"
      element={
        <RequirePermission anyOf={READ_BOOKINGS_PERMISSIONS}>
          <BookingPage />
        </RequirePermission>
      }
    />
    <Route
      path="bookings/:id"
      element={
        <RequirePermission anyOf={READ_BOOKINGS_PERMISSIONS}>
          <BookingDetailPage />
        </RequirePermission>
      }
    />
  </>
);

/**
 * BON-459 — customer PII behind its permission gate.
 *
 * The backend router is the actual control; this only stops a denied user
 * loading a page that can do nothing but 403, matching the `bookings/:id`
 * sibling.
 */
const customersElement = (
  <RequirePermission anyOf={[Permission.READ_CUSTOMERS, Permission.READ_ALL]}>
    <CustomerManagement />
  </RequirePermission>
);

/** Overview reads every scope, so it gates on the permission alone and takes
 *  its region from the address. */
const overviewElement = (
  <RequirePermission anyOf={[Permission.READ_DASHBOARDS, Permission.READ_ALL]}>
    <OverviewDashboard />
  </RequirePermission>
);

/** The operational pages, none of which carries a route guard today. */
const operationsRoutes = (
  <>
    <Route path="inventory" element={<InventoryPage />} />
    <Route path="staff" element={<StaffManagement />} />
    <Route path="staff/:id" element={<StaffEditPage />} />
    <Route path="private-events" element={<PrivateEventsPage />} />
    <Route path="private-events/:id" element={<PrivateEventDetailPage />} />
  </>
);

const marketingRoutes = (
  <>
    <Route path="marketing/*" element={<MarketingPage />} />
    <Route path="data/*" element={<DataPage />} />
  </>
);

export default function AdminRoutes() {
  return (
    <Routes>
      {/* `/admin` alone lands on the first page this viewer can open — the
          Overview is gated, and CSR, Finance and Marketing hold neither of its
          grants, so a fixed target here would 403 three roles. */}
      <Route index element={<HomeRedirect />} />
      <Route path="overview/*" element={overviewElement} />
      {bookingRoutes}
      {reviewRoutes}
      {giftCardsRoutes}
      {giftPassesRoutes}
      <Route path="customers" element={customersElement} />
      {venuesRoutes}
      {operationsRoutes}
      {marketingRoutes}
      {ticketSalesCampaignsRoutes}
      {giftPassBundlesRoutes}
      <Route path="settings/*" element={<SettingsPage />} />
      {adminDashboardsRoutes}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
